/**
 * How often the "Get RasoiX on your Home Screen" offer may be shown.
 *
 * "Not now" means *not this time* — never "never ask me again". There is deliberately no
 * permanent opt-out: the equivalent key for the order-updates prompt was retired for exactly
 * that reason (see `RETIRED_PERMANENT_DISMISSAL_KEY` in `@/domain/order/order-updates-prompt`).
 *
 * Policy:
 *   - the first dismissal costs nothing — the offer may appear again
 *   - from the second dismissal on, each one buys {@link COOLDOWN_MS} of quiet
 *   - a customer who actually installs is never asked again, and that is decided by the live
 *     display-mode check in `@/domain/install/install-capability`, never by anything stored here
 *
 * State is INDEPENDENT of the order-updates prompt. It intentionally shares that module's shape
 * but not its storage: a customer who declines notifications must not thereby silence the install
 * offer, and declining an install must not distort the push feature's dismissal signal.
 *
 * Stores two UX counters and nothing else — no identity, no order data, no push credentials.
 * Entirely client-side; no backend, no analytics.
 *
 * Every storage failure fails *open* (offer shown). Going permanently quiet because
 * `localStorage` threw once would be a worse outcome than one extra prompt.
 */

const STORAGE_KEY = "rx-install-prompt-v1";

/** Dismissals allowed before the cooldown starts applying. */
export const DISMISSALS_BEFORE_COOLDOWN = 2;

/** Quiet period after a dismissal, once the allowance above is used up. */
export const COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000;

type InstallPromptFrequencyState = {
  /** Total times the customer chose "Not now". */
  dismissals: number;
  /** Epoch ms of the most recent dismissal. */
  lastDismissedAt: number;
};

function readState(): InstallPromptFrequencyState | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<InstallPromptFrequencyState> | null;
    const dismissals = Number(parsed?.dismissals);
    const lastDismissedAt = Number(parsed?.lastDismissedAt);
    if (!Number.isFinite(dismissals) || dismissals < 0) return null;
    if (!Number.isFinite(lastDismissedAt) || lastDismissedAt < 0) return null;

    return { dismissals: Math.floor(dismissals), lastDismissedAt };
  } catch {
    // Unreadable, corrupt, or storage unavailable — treat as "never dismissed" and show.
    return null;
  }
}

/**
 * True when the install offer may be shown.
 * `now` is injectable so the cooldown is testable without faking the clock globally.
 */
export function canShowInstallPrompt(now: number = Date.now()): boolean {
  const state = readState();
  if (!state) return true;
  if (state.dismissals < DISMISSALS_BEFORE_COOLDOWN) return true;

  // A timestamp in the future (clock change, tampering) must not mute the offer indefinitely.
  if (state.lastDismissedAt > now) return true;

  return now - state.lastDismissedAt >= COOLDOWN_MS;
}

/** Records one "Not now". Silently does nothing when storage is unavailable. */
export function recordInstallPromptDismissal(now: number = Date.now()): void {
  const previous = readState();
  const next: InstallPromptFrequencyState = {
    dismissals: (previous?.dismissals ?? 0) + 1,
    lastDismissedAt: now,
  };

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Private browsing / quota — the card still closes for this visit; the customer is simply
    // offered again next time, which is the safe direction to fail in.
  }
}
